import React from "react";
import { useSelector } from "react-redux";

import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import DataTable from "examples/Tables/DataTable";
import EditCosting from "../EditCosting";

import GaugeComponent from "react-gauge-component";
import { Info } from "@mui/icons-material";

import { canEditOrder } from "../../../utils/permissions";

export default function CostingTable({ device, user }) {
  const currentOrder = useSelector((state) => state.orders.currentOrder);
  const canEdit = canEditOrder(user, currentOrder);

  const realWorkersCost = device.hours_past * currentOrder.area_hourly_rates;
  const forecastWorkersCost = device.hours_sold * currentOrder.area_hourly_rates;
  const plannedWorkersCost = device.hours_planned * currentOrder.area_hourly_rates;
  const performance =
    (device.real_purchases + realWorkersCost) / (device.purchases + forecastWorkersCost);
  const efficiency = device.hours_past / device.hours_sold;

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const costingColumns = [
    { Header: "Chiffrage", accessor: "numeral", align: "center" },
    { Header: "Prix de vente de l'appareil", accessor: "device_amount", align: "center" },
    { Header: "Coût des achats", accessor: "buyings_budget", align: "center" },
    { Header: "Nombre d'heures", accessor: "hours", align: "center" },
    { Header: "Coût main d'oeuvre ", accessor: "hours_budget", align: "center", info: "workers" },
    { Header: "Efficacité", accessor: "eff", align: "center", info: "ratio" },
    { Header: "Performance", accessor: "perf", align: "center", info: "ratio" },
  ];
  const costingRows = [
    {
      numeral: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          Prévisionnel
        </MDTypography>
      ),
      device_amount: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          -
        </MDTypography>
      ),
      buyings_budget: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device.purchases !== null
            ? new Intl.NumberFormat("fr-FR").format(device && device.purchases) + " euros"
            : "Non spécifié"}
        </MDTypography>
      ),
      hours: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device.hours_sold !== null
            ? new Intl.NumberFormat("fr-FR").format(device && device.hours_sold) + " heures"
            : "Non spécifié"}
        </MDTypography>
      ),
      hours_budget: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device.hours_sold !== null
            ? new Intl.NumberFormat("fr-FR").format(device && forecastWorkersCost) + " euros"
            : "Non spécifié"}
        </MDTypography>
      ),
    },
    {
      numeral: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          Planifiée
        </MDTypography>
      ),
      device_amount: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          -
        </MDTypography>
      ),
      buyings_budget: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          -
        </MDTypography>
      ),
      hours: (
        <MDBox lineHeight={1}>
          <MDTypography component="a" variant="button" fontWeight="medium">
            {device.hours_sold !== null
              ? new Intl.NumberFormat("fr-FR").format(device && device.hours_planned) + " heures"
              : "Non spécifié"}
          </MDTypography>
          <MDTypography mt={0.15} display="block" variant="caption">
            {device && device.hours_sold !== null
              ? device.hours_sold - device.hours_planned >= 0
                ? "Il reste"
                : "Dépassement de"
              : ""}
          </MDTypography>
          <MDTypography
            color={device.hours_sold - device.hours_planned >= 0 ? "success" : "error"}
            mt={0.05}
            display="block"
            variant="string"
            fontWeight="medium"
          >
            {device && device.hours_sold !== null
              ? device.hours_sold - device.hours_planned >= 0
                ? new Intl.NumberFormat("fr-FR").format(device.hours_sold - device.hours_planned) +
                  `${
                    device.hours_sold - device.hours_planned !== 0 &&
                    device.hours_sold - device.hours_planned !== 1
                      ? " heures"
                      : " heure"
                  }`
                : new Intl.NumberFormat("fr-FR").format(
                    -1 * (device.hours_sold - device.hours_planned)
                  ) +
                  `${
                    device.hours_sold - device.hours_planned !== 0 &&
                    device.hours_sold - device.hours_planned !== -1
                      ? " heures"
                      : " heure"
                  }`
              : "Non spécifié"}
          </MDTypography>
        </MDBox>
      ),
      hours_budget: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device.hours_planned !== null
            ? new Intl.NumberFormat("fr-FR").format(device && plannedWorkersCost) + " euros"
            : "Non spécifié"}
        </MDTypography>
      ),
      eff: (
        <MDBox sx={{ width: 200 + "px" }}>
          <GaugeComponent
            arc={{
              subArcs: [
                {
                  limit: 0.05,
                  color: "#004400", // Dark green
                },
                {
                  limit: 0.1,
                  color: "#005000", // Dark green
                },
                {
                  limit: 0.15,
                  color: "#005C00", // Dark green
                },
                {
                  limit: 0.2,
                  color: "#006800", // Dark green
                },
                {
                  limit: 0.25,
                  color: "#007400", // Dark green
                },
                {
                  limit: 0.3,
                  color: "#008000", // Dark green
                },
                {
                  limit: 0.35,
                  color: "#008C00", // Dark green
                },
                {
                  limit: 0.4,
                  color: "#009800", // Dark green
                },
                {
                  limit: 0.45,
                  color: "#00A400", // Dark green
                },
                {
                  limit: 0.5,
                  color: "#00B000", // Dark green
                },
                {
                  limit: 0.55,
                  color: "#00BC00", // Dark green
                },
                {
                  limit: 0.6,
                  color: "#00C800", // Dark green
                },
                {
                  limit: 0.65,
                  color: "#00D400", // Dark green
                },
                {
                  limit: 0.7,
                  color: "#00E000", // Dark green
                },
                {
                  limit: 0.75,
                  color: "#00EC00", // Dark green
                },
                {
                  limit: 0.8,
                  color: "#00F800", // Almost pure green
                },
                {
                  limit: 0.85,
                  color: "#00FF00", // Pure green
                },
                {
                  limit: 0.9,
                  color: "#00FF00", // Pure green
                },
                {
                  limit: 0.95,
                  color: "#4CFF4C", // Slightly lighter green
                },
                {
                  limit: 1,
                  color: "#66FF66", // Slightly lighter green
                  showTick: true,
                },
                {
                  limit: 1.05,
                  color: "#FFEE00", // Yellow
                },
                {
                  limit: 1.1,
                  color: "#FFCF00", // Darker yellow
                },
                {
                  limit: 1.15,
                  color: "#FFA500", // Orange
                },
                {
                  limit: 1.2,
                  color: "#FF8000", // Darker orange
                },
                {
                  limit: 1.25,
                  color: "#FF4500", // Red-orange
                },
                {
                  limit: 1.3,
                  color: "#FF0000", // Red
                },
              ],
            }}
            id={"eff-gauge-" + darkMode}
            minValue={0}
            maxValue={1.3}
            value={device && device.hours_sold !== null && device.hours_past !== null && efficiency}
            style={{
              margin: "0 auto",
              width: 250 + "px",
              position: "absolute",
              left: 0,
              right: 0,
              top: -48.935,
            }}
            labels={{
              valueLabel: {
                /* matchColorWithArc: true, */
                style: {
                  fontSize: "35px",
                  fill: darkMode ? "white" : "#344767",
                  textShadow: "none",
                  fontWeight: "bold",
                  textAnchor: "middle",
                },
              },
            }}
          />
        </MDBox>
      ),
      perf: (
        <MDBox sx={{ width: 200 + "px" }}>
          <GaugeComponent
            arc={{
              subArcs: [
                {
                  limit: 0.05,
                  color: "#004400", // Dark green
                },
                {
                  limit: 0.1,
                  color: "#005000", // Dark green
                },
                {
                  limit: 0.15,
                  color: "#005C00", // Dark green
                },
                {
                  limit: 0.2,
                  color: "#006800", // Dark green
                },
                {
                  limit: 0.25,
                  color: "#007400", // Dark green
                },
                {
                  limit: 0.3,
                  color: "#008000", // Dark green
                },
                {
                  limit: 0.35,
                  color: "#008C00", // Dark green
                },
                {
                  limit: 0.4,
                  color: "#009800", // Dark green
                },
                {
                  limit: 0.45,
                  color: "#00A400", // Dark green
                },
                {
                  limit: 0.5,
                  color: "#00B000", // Dark green
                },
                {
                  limit: 0.55,
                  color: "#00BC00", // Dark green
                },
                {
                  limit: 0.6,
                  color: "#00C800", // Dark green
                },
                {
                  limit: 0.65,
                  color: "#00D400", // Dark green
                },
                {
                  limit: 0.7,
                  color: "#00E000", // Dark green
                },
                {
                  limit: 0.75,
                  color: "#00EC00", // Dark green
                },
                {
                  limit: 0.8,
                  color: "#00F800", // Almost pure green
                },
                {
                  limit: 0.85,
                  color: "#00FF00", // Pure green
                },
                {
                  limit: 0.9,
                  color: "#00FF00", // Pure green
                },
                {
                  limit: 0.95,
                  color: "#4CFF4C", // Slightly lighter green
                },
                {
                  limit: 1,
                  color: "#66FF66", // Slightly lighter green
                  showTick: true,
                },
                {
                  limit: 1.05,
                  color: "#FFEE00", // Yellow
                },
                {
                  limit: 1.1,
                  color: "#FFCF00", // Darker yellow
                },
                {
                  limit: 1.15,
                  color: "#FFA500", // Orange
                },
                {
                  limit: 1.2,
                  color: "#FF8000", // Darker orange
                },
                {
                  limit: 1.25,
                  color: "#FF4500", // Red-orange
                },
                {
                  limit: 1.3,
                  color: "#FF0000", // Red
                },
              ],
            }}
            // 201.73px & 131.17px
            id={"perf-gauge-" + darkMode}
            minValue={0}
            maxValue={1.3}
            value={
              device &&
              device.real_purchases !== null &&
              device.purchases !== null &&
              device.hours_sold !== null &&
              currentOrder.area_hourly_rates !== null &&
              device.hours_past !== null &&
              performance
            }
            style={{
              margin: "0 auto",
              width: 250 + "px",
              position: "absolute",
              left: 0,
              right: 0,
              top: -48.935,
            }}
            labels={{
              valueLabel: {
                /* matchColorWithArc: true, */
                style: {
                  fontSize: "35px",
                  fill: darkMode ? "white" : "#344767",
                  textShadow: "none",
                  fontWeight: "bold",
                  textAnchor: "middle",
                },
              },
            }}
          />
        </MDBox>
      ),
    },
    {
      numeral: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          Réel
        </MDTypography>
      ),
      device_amount: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device.price !== null
            ? new Intl.NumberFormat("fr-FR").format(device && device.price) + " euros"
            : "Non spécifié"}
        </MDTypography>
      ),
      buyings_budget: (
        <MDTypography component="a" variant="button" fontWeight="medium">
          {device.real_purchases !== null
            ? new Intl.NumberFormat("fr-FR").format(device && device.real_purchases) + " euros"
            : "Non spécifié"}
        </MDTypography>
      ),
      hours: (
        <MDBox lineHeight={1}>
          <MDTypography component="a" variant="button" fontWeight="medium">
            {device && device.hours_past !== null
              ? new Intl.NumberFormat("fr-FR").format(device.hours_past) + " heures passées"
              : "Non spécifié"}
          </MDTypography>
        </MDBox>
      ),
      hours_budget: (
        <MDBox lineHeight={1}>
          <MDTypography display="block" variant="string" fontWeight="medium" mb={0.25}>
            {device.hours_past !== null
              ? new Intl.NumberFormat("fr-FR").format(device && realWorkersCost) + " euros"
              : "Non spécifié"}
          </MDTypography>
        </MDBox>
      ),
    },
  ];
  return (
    <Grid item xs={12} pt={3} sx={{ height: "100%" }}>
      <Card sx={{ height: "100%" }}>
        <MDBox
          mx={2}
          mt={-3}
          py={1.5}
          px={2}
          variant="gradient"
          borderRadius="lg"
          sx={{
            background: !darkMode ? "linear-gradient(195deg, #364c70, #273956)" : "white",
            boxShadow: darkMode ? "0 1px 4px 1px white" : "0 1px 4px 1px #344767",
            display: "flex",
            verticalAlign: "middle",
          }}
        >
          <MDTypography
            variant="h5"
            style={{
              width: "50%",
              lineHeight: "2.4rem",
              display: "tableCell",
              verticalAlign: "middle",
            }}
            color={darkMode ? "inherit" : "white"}
          >
            Chiffrage
          </MDTypography>
          {canEdit && (
              <Grid style={{ textAlign: "right", width: "50%" }}>
                <EditCosting device={device} edit={true} renderStep={0} />
              </Grid>
            )}
        </MDBox>
        <MDBox
          pt={0.75}
          pl={0}
          sx={{
            height: "100%",
            "div.MuiTableContainer-root": {
              height: "100%",
              display: "flex",
            },
          }}
        >
          <DataTable
            table={{ columns: costingColumns, rows: costingRows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      </Card>
    </Grid>
  );
}
